import React from 'react';
import { Button } from 'react-bootstrap';
import { RpmSeverityBadge } from './RpmThresholdAlertListItem';
import {
    formatAlertTs,
    isAlertOverdue,
    overdueLabel,
    dueRelativeLabel,
} from '../../utils/rpmAlertQueueUtils';
import { RPM_ALERT_SEVERITIES } from '../../constants/other';

const resolveAlertSeverity = (alert) => {
    if (!alert) {
        return null;
    }
    if (alert.severity) {
        return RPM_ALERT_SEVERITIES.find((entry) => entry.key === alert.severity) || null;
    }
    return RPM_ALERT_SEVERITIES.find((entry) => entry.typeId == alert.urgent_todo_type_id) || null;
};

const getTodoTypeTitle = (alert, urgentTypes) => {
    const name = (alert.urgent_todo_type_name || '').trim();
    if (name) {
        return name;
    }
    const typeEntry = (urgentTypes || []).find((entry) => entry.id == alert.urgent_todo_type_id);
    return typeEntry ? typeEntry.name : 'RPM Threshold Alert';
};

/** Same segments as queue `buildAlertMetaLine`, with chart overdue styling. */
const ChartAlertMeta = ({ receivedTs, dueTs }) => {
    if (!receivedTs && !dueTs) {
        return null;
    }
    const overdue = dueTs && isAlertOverdue(dueTs);
    const statusLabel = dueTs
        ? (overdue ? overdueLabel(dueTs) : dueRelativeLabel(dueTs))
        : '';

    return (
        <span className="rpm-chart-alert-card__due-meta">
            {receivedTs &&
                <span className="rpm-chart-alert-card__due-text">
                    Received {formatAlertTs(receivedTs)}
                </span>
            }
            {receivedTs && dueTs &&
                <span className="rpm-chart-alert-card__due-sep"> · </span>
            }
            {dueTs &&
                <span className="rpm-chart-alert-card__due-text">
                    Due {formatAlertTs(dueTs)}
                </span>
            }
            {statusLabel &&
                <span className="rpm-chart-alert-card__due-sep"> · </span>
            }
            {statusLabel &&
                <span className={overdue ? 'rpm-chart-alert-card__overdue-text' : 'rpm-chart-alert-card__due-text'}>
                    {statusLabel}
                </span>
            }
        </span>
    );
};

const RpmThresholdChartAlertCard = ({
    alert,
    urgentTypes,
    expanded,
    onToggleExpand,
    onComplete,
    completingTodoId,
}) => {
    const severity = resolveAlertSeverity(alert);
    const severityKey = severity ? severity.key.toLowerCase() : 'unknown';
    const receivedTs = alert.create_ts || alert.received_ts;
    const dueTs = alert.due_date_ts;
    const todoId = alert.todo_id || alert.id;
    const isCompleting = completingTodoId != null && completingTodoId == todoId;
    const overdue = dueTs && isAlertOverdue(dueTs);
    const todoTypeTitle = getTodoTypeTitle(alert, urgentTypes);
    const description = (alert.description || '').trim();
    const hasDetails = !!description;
    return (
        <div className={`rpm-chart-alert-card rpm-chart-alert-card--${severityKey}`}>
            <div className="rpm-chart-alert-card__row1">
                <div className="rpm-chart-alert-card__type">
                    <RpmSeverityBadge severity={severity} />
                    <span className="rpm-chart-alert-card__title">{todoTypeTitle}</span>
                </div>
                <div className="rpm-chart-alert-card__row1-actions">
                    {overdue &&
                        <span className="rpm-alert-pastdue-pill">
                            <i className="fa fa-clock-o" aria-hidden="true" /> PAST DUE
                        </span>
                    }
                    <Button
                        bsStyle="success"
                        className="rpm-chart-alert-complete-btn"
                        disabled={isCompleting}
                        onClick={() => onComplete(alert)}
                    >
                        {isCompleting ? 'Completing…' : 'Complete'}
                    </Button>
                </div>
            </div>
            <div className="rpm-chart-alert-card__row2">
                <ChartAlertMeta receivedTs={receivedTs} dueTs={dueTs} />
                {hasDetails &&
                    <button
                        type="button"
                        className="rpm-chart-alert-card__toggle"
                        onClick={() => onToggleExpand(todoId)}
                    >
                        {expanded ? 'Hide details' : 'View details'}
                        <i
                            className={`fa ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} rpm-chart-alert-card__toggle-icon`}
                            aria-hidden="true"
                        />
                    </button>
                }
            </div>
            {expanded && hasDetails &&
                <div className="rpm-chart-alert-card__details">{description}</div>
            }
        </div>
    );
};

export default RpmThresholdChartAlertCard;
